import { useEffect, useState } from 'react';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { ShopifyCartProps } from '#/globalTypes';
import Link from 'next/link';
import { ParsedUrlQuery } from 'querystring';
import CartItems from '@/components/molecules/CartItem';
import { useUser } from '@/context/UserContext';
import { useCart } from '@/context/CartContext';
import useLayoutData from '@/hooks/LayoutData';
import useFetchContent from '@/hooks/Content';
import { NextSeo } from 'next-seo';
import ActiveSubscriptionModal from '@/components/organisms/ActiveSubscriptionModal';
import logger from '@/lib/logger';
import {
  cartPageGtm,
  checkoutButtonClickGtm,
  removeItemGtm,
  clickHereButtonGtm,
} from '@/lib/Gtmlib';
import postApiCalls from '@/lib/api';
import { getCustomerAccessToken } from '@/lib/Cookielib';
import { getUrls } from '@/lib/Urls';


interface Params extends ParsedUrlQuery {
  authRedirect: string;
}

interface Props {
  customerAccessToken?: string | null;
  checkoutRedirect?: boolean | null;
  eligibility: boolean;
}

export default function Cart(props: Props) {
  const { customerAccessToken, checkoutRedirect } = props;
  const [data, setData] = useState({
    eligibility: false,
  });
  const [loading, setLoading] = useState(true);
  let eligibility = false;

  const router = useRouter();
  const [showHasPlan, setShowHasPlan] = useState<boolean>(false);
  const { cartItems, removeItemsFromCart, checkoutWebUrl } = useCart();
  const url = getUrls();
  const showHasPlanModal = (show: boolean) => {
    removeItemsFromCart().then(() => setShowHasPlan(show));
  };

  useEffect(() => {
    setLoading(true);
    const fetchEligibility = async () => {
      if (customerAccessToken) {
        //Check for eligibility
        try {
          const response = await postApiCalls({
            url: `/api/auth/eligiblecheck`,
            body: {
              customerAccessToken,
            },
          });
          const eligible = response.data.userEligibility;
          setData({
            eligibility: eligible,
          });
          setShowHasPlan(!eligible);
        } catch (err: any) {
          logger.error(err);
          setShowHasPlan(false);
        }
      } else {
        setShowHasPlan(false);
      }
      setLoading(false);
    };

    fetchEligibility();
  }, []);

  if (!loading) {
    eligibility = data.eligibility;
  }

  const { authenticationUserServerSide, loggedIn } = useUser();
  if (!loggedIn) authenticationUserServerSide(customerAccessToken);

  if (customerAccessToken && eligibility && checkoutRedirect) {
    const checkoutData = JSON.parse(localStorage.getItem('checkout') as string);
    if (checkoutData?.checkoutWebUrl) {
      router.push(checkoutData.checkoutWebUrl);
    }
  }

  useLayoutData();
  useFetchContent();

  const [filteredItems, setFilteredItems] =
    useState<ShopifyCartProps[]>(cartItems);

  useEffect(() => {
    setFilteredItems(cartItems);
  }, [filteredItems, cartItems]);

  useEffect(() => {
    cartPageGtm();
  }, []);

  const removeItem = (slug: string) => {
    removeItemGtm(slug);

    const newItems = filteredItems?.filter((item) => item.productSlug !== slug);
    setFilteredItems(newItems);
    removeItemsFromCart();
  };

  const doCheckout = () => {
    checkoutButtonClickGtm();

    if (eligibility && customerAccessToken) {
      router.push(checkoutWebUrl);
    } else {
      router.push(
        `${url.dsStore}/account/register?return_url=${window.location.origin}${router.asPath}/checkout`
      );
    }
  };

  const goToDirectStoreCart = () => {
    clickHereButtonGtm();

    router.push(`https://buy.gazelle.com/cart`);
  };

  return loading ? (
    <></>
  ) : (
    <>
      <NextSeo title="Your Rental Cart - Gazelle Certified" />
      <section
        className={'w-full px-4 py-2 xl:px-48 xl:py-10 flex flex-col'}
        data-cy="cart-page"
      >
        <h3
          className={
            'font-medium flex flex-col xl:flex-row text-xl xl:text-2xl text-start'
          }
        >
          {filteredItems.length ? 'Your Rentals' : 'Your cart is empty'}
          {filteredItems.length > 0 && (
            <>
              <span className={'text-lg xl:text-2xl ml-2'}>
                {filteredItems?.length === 0
                  ? ' (0 Items)'
                  : filteredItems?.length === 1
                  ? ' (1 Item)'
                  : `(${filteredItems?.length} items)`}
              </span>
            </>
          )}
        </h3>
        {filteredItems.length > 0 && (
          <div className={'text-sm/[16px] text-[#5B5B5B] mb-4'}>
            This cart is for Gazelle Flex rentals only. For your other items{' '}
            <button
              onClick={goToDirectStoreCart}
              data-tracking-id="direct-store-link-cart"
              className={'text-[#FF6900]'}
            >
              click here
            </button>
            .
          </div>
        )}
        <div
          className={
            'w-full flex flex-col-reverse xl:flex-row justify-between items-start'
          }
        >
          <div className={'w-full xl:w-2/3 flex flex-col xl:mr-2'}>
            {filteredItems?.map((item) => (
              <CartItems
                key={item?.productSlug}
                item={item}
                removeItem={removeItem}
              />
            ))}
            {!filteredItems.length && (
              <>
                <div className={'text-sm/[16px] text-[#5B5B5B] my-4'}>
                  You haven’t added any rentals to your cart.
                </div>
                <div className={'text-sm/[16px] text-[#5B5B5B] mb-4'}>
                  Browse rentals{' '}
                  <Link href={'/'} className={'text-[#FF6900]'}>
                    here
                  </Link>
                  .
                </div>
              </>
            )}
          </div>
          {filteredItems.length > 0 && (
            <div
              className={
                'w-full mb-2 xl:w-1/3 flex flex-col rounded-lg border-2 border-gray-200 p-4 xl:ml-2'
              }
              data-cy={'total-amount-container'}
            >
              <div
                className={
                  'w-full h-12 py-2 flex flex-row items-center justify-between border-b border-b-gray-200'
                }
              >
                <p className={'font-medium mb-1'}>Order Subtotal</p>
                <p className={'font-medium mb-1'}>
                  $
                  {cartItems?.length
                    ? cartItems[0]?.subtotalPrice?.toFixed(2)
                    : 0.0}
                </p>
              </div>
              <div
                className={
                  'w-full h-12 py-2 flex flex-row items-center justify-between border-b border-b-gray-200'
                }
              >
                <p className={'font-medium mb-1'}>Shipping</p>
                <p className={'font-medium mb-1 uppercase'}>Free</p>
              </div>
              <div
                className={
                  'w-full h-12 py-2 flex flex-row items-center justify-between'
                }
              >
                <p className={'font-medium mb-1'}>Total</p>
                <p
                  className={'font-medium mb-1'}
                  data-testid={'overall-total-price'}
                >
                  $
                  {cartItems?.length
                    ? cartItems[0]?.subtotalPrice?.toFixed(2)
                    : 0.0}
                </p>
              </div>
              <p className={'text-sm my-2 font-light'}>
                Taxes calculated at checkout.
              </p>

              <button
                disabled={loggedIn && !eligibility}
                onClick={() => doCheckout()}
                data-cy={'go-to-checkout'}
                data-tracking-id="checkout-cart"
                className={
                  'w-full text-white bg-primary hover:bg-hoverPrimary disabled:bg-slate-200 focus:ring-4 focus:outline-none focus:ring-white rounded-lg px-5 py-3 text-lg text-center my-4 font-bold'
                }
              >
                Checkout
              </button>
            </div>
          )}
        </div>
      </section>
      {showHasPlan && (
        <ActiveSubscriptionModal
          onClose={() => showHasPlanModal(false)}
          showText={true}
          customerAccessToken={customerAccessToken}
          activeRental={true}
          headerText={`Looks like you already have a rental`}
        />
      )}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const customerAccessToken = await getCustomerAccessToken(
    context.req,
    context.res
  );

  const { params } = context;
  const { slug } = params as Params;
  try {
    return {
      props: {
        customerAccessToken: customerAccessToken ?? null,
        eligibility: null,
        checkoutRedirect: (slug && slug[0]) === 'checkout',
      },
    };
  } catch (error: any) {
    logger.error(error);
    return {
      props: {
        customerAccessToken: customerAccessToken ?? null,
        eligibility: null,
        checkoutRedirect: null,
      },
    };
  }
};